// REF-6708: the reviewed classifiers that may put an error's CODE into Sentry metadata.
//
// A code read straight off a caught error is arbitrary text at the catch boundary — anything that throws
// can set `code`, and a backend body can carry any string — so Sentry extras / fingerprints never take it
// raw. These return the code only when it is on a fixed, reviewed list, `"other"` for any other string,
// and `undefined` when there is no code at all. Like `httpStatusOf`, they read the error inside a try, so
// an odd or hostile value cannot throw or leak through them. They are for Sentry metadata only; console
// logs take `httpStatusOf(error)` and nothing else (docs/rules/error-handling.md).
//
// Adding a value is a reviewed change: it must be a fixed machine code, never free text.

import { getMiroErrorCode, MIRO_ERROR } from "utils/miro";
import { sanitizeRoute } from "utils/privacy/telemetryUrl";

export const OTHER_ERROR_CODE = "other";

// axios's own error codes: the six the installed 0.26.1 sets (`ECONNABORTED`, `ETIMEDOUT`, `E_JSON_PARSE`,
// `ERR_FR_TOO_MANY_REDIRECTS`, `ERR_REQUEST_ABORTED`, `ERR_PARSE_TIMEOUT`), then the 1.x `AxiosError`
// constants, listed so an upgrade does not turn every code into "other". 0.26's browser "Network Error"
// carries no code at all; a propagated Node code (`ECONNRESET`) is not axios's and becomes "other".
const AXIOS_ERROR_CODES: ReadonlySet<string> = new Set([
  "ECONNABORTED",
  "ETIMEDOUT",
  "E_JSON_PARSE",
  "ERR_FR_TOO_MANY_REDIRECTS",
  "ERR_REQUEST_ABORTED",
  "ERR_PARSE_TIMEOUT",
  "ERR_BAD_OPTION_VALUE",
  "ERR_BAD_OPTION",
  "ERR_NETWORK",
  "ERR_DEPRECATED",
  "ERR_BAD_RESPONSE",
  "ERR_BAD_REQUEST",
  "ERR_CANCELED",
  "ERR_NOT_SUPPORT",
  "ERR_INVALID_URL",
]);

// The backend's stable `responseData.error_code` / `reason_code` values the app already maps to copy
// (the flat keys of the `errors` locale namespace), plus `must_set_password` (HttpClient's redirect).
const BACKEND_ERROR_CODES: ReadonlySet<string> = new Set([
  "insufficient_credits",
  "subscription_expired",
  "trial_ended",
  "feature_not_in_plan",
  "seat_limit_reached",
  "unsupported_language",
  "invalid_image",
  "rate_limited",
  "maintenance",
  "member_credit_limit",
  "same_plan",
  "trial_seats_blocked",
  "max_seats_exceeded",
  "plan_not_seatable",
  "price_not_found",
  "user_not_found",
  "team_creation_failed",
  "upgrade_failed",
  "addon_not_found",
  "admin_required",
  "paid_subscription_required",
  "seat_count_unchanged",
  "invalid_seat_count",
  "seat_count_too_low",
  "no_active_subscription",
  "invalid_email",
  "invalid_role",
  "cannot_invite_self",
  "already_in_team",
  "invite_create_failed",
  "invite_email_failed",
  "invite_not_found",
  "invite_cancelled",
  "invite_already_accepted",
  "invite_expired",
  "invite_awaiting_seat",
  "invalid_member_id",
  "cannot_remove_self",
  "not_team_member",
  "invalid_invite_id",
  "invite_not_your_team",
  "invite_not_pending",
  "invite_already_cancelled",
  "invalid_allocation",
  "allocation_negative",
  "allocation_below_spent",
  "allocation_exceeds_pool",
  "invalid_permissions",
  "unknown_permission_node",
  "permission_exceeds_max",
  "admin_perms_locked",
  "super_admin_required",
  "invalid_policy",
  "seats_below_active_usage",
  "share_mode_conflict",
  "share_not_found",
  "share_invalid_mode",
  "share_users_required",
  "share_self_share",
  "share_not_teammates",
  "share_too_many_recipients",
  "must_set_password",
]);

const categorize = (read: () => unknown, allowed: ReadonlySet<string>, other: string = OTHER_ERROR_CODE): string | undefined => {
  try {
    const code = read();
    if (typeof code !== "string") return undefined;

    return allowed.has(code) ? code : other;
  } catch {
    return undefined;
  }
};

/** axios's `error.code` when it is a known axios code, `"other"` for any other string, else `undefined`. */
export const axiosCodeCategory = (error: unknown): string | undefined => categorize(() => (error as { code?: unknown } | null | undefined)?.code, AXIOS_ERROR_CODES);

/** The backend's `responseData.error_code` when it is a reviewed code, `"other"` for any other string, else `undefined`. */
export const backendErrorCodeCategory = (error: unknown): string | undefined =>
  categorize(() => (error as { response?: { data?: { responseData?: { error_code?: unknown } } } } | null | undefined)?.response?.data?.responseData?.error_code, BACKEND_ERROR_CODES);

// ── Miro refusals (REF-6702) ─────────────────────────────────────────────────────────────────────────

export const MIRO_OTHER_ERROR_CODE = "miro.other";

// `getMiroErrorCode` returns whatever string the response body carries, so analytics never takes it raw.
// The reviewed list: the `MIRO_ERROR` constants, the codes the Miro surfaces branch on, and the codes with
// their own `miro:errors.*` / `miro:item_errors.*` copy (the FE-only fallbacks `generic`, `connect_failed`,
// `disconnect_failed` and `status_failed` are copy keys, not codes).
const MIRO_ERROR_CODES: ReadonlySet<string> = new Set([
  ...Object.values(MIRO_ERROR),
  "miro.job_not_found",
  "miro.job_not_started",
  "miro.too_many_items",
  "miro.nothing_to_import",
  "miro.nothing_to_export",
  "miro.board_busy",
  "miro.canvas_not_found",
  "miro.frame_not_found",
  "miro.comments_unavailable",
  "miro.download_failed",
  "miro.image_invalid",
  "miro.image_too_large",
  "miro.unsupported_item",
  "miro.source_not_allowed",
  "miro.moodboard_file_not_allowed",
  "miro.moodboard_not_permitted",
  "miro.moodboard_start_failed",
  "miro.moodboard_too_many_active",
]);

/** A Miro endpoint's refusal code (as `getMiroErrorCode` reads it) when reviewed, `"miro.other"` for any other string, else `undefined`. */
export const miroErrorCodeCategory = (error: unknown): string | undefined => categorize(() => getMiroErrorCode(error), MIRO_ERROR_CODES, MIRO_OTHER_ERROR_CODE);

// ── request facts the HTTP interceptor reports (REF-6708 Wave 3, area c) ──────────────────────────────

const HTTP_METHODS: ReadonlySet<string> = new Set(["get", "post", "put", "patch", "delete", "head", "options"]);
// An opaque correlation id: letters, digits and `._:-`, at most 128 characters. Anything else is not one.
const REQUEST_ID_PATTERN = /^[A-Za-z0-9._:-]{1,128}$/;

type RequestShape =
  | {
      config?: { url?: unknown; method?: unknown };
      response?: { headers?: Record<string, unknown>; data?: { responseData?: Record<string, unknown> } };
    }
  | null
  | undefined;

const read = <T>(get: () => T): T | undefined => {
  try {
    return get();
  } catch {
    return undefined;
  }
};

/**
 * The failed request's path through `sanitizeRoute` — the same pass the Sentry adapter applies — else `undefined`.
 * The adapter sanitises it again. That second pass is a no-op for every ordinary route; the one known
 * exception (a dot segment hiding a scheme-like prefix, `./foo:bar` → `foo:bar` → `[redacted]`) only
 * redacts MORE, never less.
 */
export const requestPathOf = (error: unknown): string | undefined =>
  read(() => {
    const url = (error as RequestShape)?.config?.url;

    return typeof url === "string" ? sanitizeRoute(url) : undefined;
  });

/** The failed request's HTTP verb (lower-case, as axios stores it) when it is a standard one, `"other"` for any other string, else `undefined`. */
export const httpMethodOf = (error: unknown): string | undefined => categorize(() => (error as RequestShape)?.config?.method, HTTP_METHODS);

/**
 * The response's request id when it is shaped like an opaque id, else `undefined`. The header is chosen exactly
 * as the interceptor always chose it (`x-request-id`, falling back to `x-correlation-id` only when absent); a
 * malformed `x-request-id` is dropped, not replaced. A format filter, not a secrecy guarantee — it trusts the
 * header's provenance (our own edge).
 */
export const requestIdOf = (error: unknown): string | undefined =>
  read(() => {
    const headers = (error as RequestShape)?.response?.headers;
    const id = headers?.["x-request-id"] ?? headers?.["x-correlation-id"];

    return typeof id === "string" && REQUEST_ID_PATTERN.test(id) ? id : undefined;
  });

/**
 * The HTTP status a resumable GCS upload failed with (`utils/resumableUpload`'s `ResumableUploadError.status`,
 * set on the error itself, not on an axios `response`) when it is an integer 100–599, else `undefined`.
 */
export const uploadStatusOf = (error: unknown): number | undefined =>
  read(() => {
    const status = (error as { status?: unknown } | null | undefined)?.status;

    return typeof status === "number" && Number.isInteger(status) && status >= 100 && status <= 599 ? status : undefined;
  });

/** The bytes GCS had confirmed when a resumable upload stopped (`ResumableUploadError.confirmedBytes`) when it is a non-negative safe integer, else `undefined`. */
export const uploadConfirmedBytesOf = (error: unknown): number | undefined =>
  read(() => {
    const bytes = (error as { confirmedBytes?: unknown } | null | undefined)?.confirmedBytes;

    return typeof bytes === "number" && Number.isSafeInteger(bytes) && bytes >= 0 ? bytes : undefined;
  });

// ── the 402 paywall payload (docs/features/billing-and-credits.md → "Insufficient-credit popup") ────────

const PAYWALL_MESSAGE_TYPES: ReadonlySet<string> = new Set(["popup", "message"]);
// The popup types the app renders (WarningSnackbar) plus the upgrade package codes it maps (WarningSnackbar.utils).
const PAYWALL_POPUP_TYPES: ReadonlySet<string> = new Set([
  "end_trial",
  "past_due",
  "contact_us",
  "contact_form",
  "contact_account_manager",
  "member_credit_limit",
  "design_starter",
  "photoshoot_starter",
  "design_pro",
  "design_studio",
  "creative_suite",
  // Legacy plan codes the slice and the interceptor still document (snackbarSlice, HttpClient).
  "essentials",
  "prestige",
]);
const PAYWALL_POPUP_REASONS: ReadonlySet<string> = new Set(["credits", "permissions"]);

const paywallField = (error: unknown, field: string) => () => (error as RequestShape)?.response?.data?.responseData?.[field];

// An explicit empty value is the same "absent" the interceptor already reports for an omitted one (`?? ""`),
// and `PopupReason` / the snackbar slice use `""` as their default — so it stays `""`, never `"other"`.
const paywallCategory = (get: () => unknown, allowed: ReadonlySet<string>, normalise: (code: string) => string = (code) => code): string | undefined => {
  const value = read(get);
  if (value === "") return "";

  return categorize(() => (typeof value === "string" ? normalise(value) : value), allowed);
};

/** The 402's `message_type` when reviewed, `"other"` for any other string, else `undefined`. */
export const paywallMessageTypeOf = (error: unknown): string | undefined => paywallCategory(paywallField(error, "message_type"), PAYWALL_MESSAGE_TYPES);

/** The 402's `popup_type` (lower-cased, as `getPackageTypeFromPopupType` matches it) when reviewed, `"other"` for any other string, else `undefined`. */
export const paywallPopupTypeOf = (error: unknown): string | undefined => paywallCategory(paywallField(error, "popup_type"), PAYWALL_POPUP_TYPES, (code) => code.toLowerCase());

/** The 402's `popup_reason` when reviewed, `"other"` for any other string, else `undefined`. */
export const paywallPopupReasonOf = (error: unknown): string | undefined => paywallCategory(paywallField(error, "popup_reason"), PAYWALL_POPUP_REASONS);

/** The 402's `required_credits` / `remaining_credits` when it is a finite number, else `null` (the interceptor's existing shape). */
export const paywallCreditsOf = (error: unknown, field: "required_credits" | "remaining_credits"): number | null => {
  const value = read(paywallField(error, field));

  return typeof value === "number" && Number.isFinite(value) ? value : null;
};
